import { useEffect, useState } from "react";
import { ChevronDown, ExternalLink, Tags } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useUserContext } from "../context/UserContext";
import { useTranslation } from "../context/LanguageContext";
import { hasBestDealsAccess } from "../utils/planUpgrade";
import { track } from "../utils/analytics";
import { getResale, isProRequiredError, type ResaleResult } from "../services/ResaleService";
import {
  amazonView,
  ebayView,
  formatRelative,
  formatShortDate,
  formatUsd,
  formatUsdCents,
  latestFetchedAt,
  noMarketData,
  pickResaleCode,
  type MarketView,
} from "./ResaleCard.logic";

// Web port of pinto's ResaleCard: what the product sells for on Amazon (Keepa
// Buy Box) and eBay (median of recent sold listings), from POST /v1/resale.
// Pro only; others see an upgrade row. Hidden when neither market has a price.

interface ResaleCardProps {
  product: { sku?: string | null; upc?: string | null; name?: string | null; msrp?: number | null };
  buyPrice: number;
  units: number;
  retailer: string;
  onUpgrade: () => void;
}

export default function ResaleCard({ product, buyPrice, units, retailer, onUpgrade }: ResaleCardProps) {
  const { user } = useUserContext();
  const { t } = useTranslation();
  // Most rows have no UPC (retailer item IDs), so the title is what makes a
  // product priceable: eBay searches it and Amazon offers a keyword search.
  const code = pickResaleCode(product);
  const title = (product.name || "").trim();
  // Same rule as the API's ProUserMiddleware: admin or a Pro/Starter/Enterprise plan.
  const isPro = hasBestDealsAccess(user);
  const canFetch = !!user && isPro && !!title && buyPrice > 0;

  const query = useQuery<ResaleResult>({
    // Session cache per product and the price it was checked at (the server gates on it).
    queryKey: ["resale", code || title, Math.round(buyPrice * 100)],
    queryFn: () =>
      getResale({
        upc: code,
        title,
        buyPrice,
        msrp: product.msrp || undefined,
        units: Math.max(1, Math.floor(units || 0)),
        retailer,
      }),
    enabled: canFetch,
    staleTime: Infinity,
    gcTime: 60 * 60 * 1000,
    retry: false,
  });

  if (!user || !title || buyPrice <= 0) return null;

  if (!isPro || isProRequiredError(query.error)) {
    return (
      <button
        type="button"
        onClick={onUpgrade}
        className="flex w-full items-center gap-3 rounded-xl border border-gray-100 bg-gray-50/80 px-3 py-2.5 text-left transition-colors hover:bg-gray-100 dark:border-dark-700 dark:bg-dark-800 dark:hover:bg-dark-700"
      >
        <span className="grid h-8 w-8 flex-shrink-0 place-items-center rounded-lg bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-300">
          <Tags className="h-4 w-4" />
        </span>
        <span className="min-w-0 flex-1 text-[13px] font-medium text-gray-700 dark:text-dark-200">{t("resale.locked")}</span>
        <span className="rounded-full bg-purple-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">{t("resale.pro")}</span>
      </button>
    );
  }

  return <ResalePrices result={query.data} loading={query.isLoading} failed={!!query.error} retailer={retailer} />;
}

function ResalePrices({ result, loading, failed, retailer }: { result?: ResaleResult; loading: boolean; failed: boolean; retailer: string }) {
  const { t, currentLanguage } = useTranslation();
  const [salesOpen, setSalesOpen] = useState(false);

  const unavailable: MarketView = { kind: "message", message: t("resale.unavailable") };
  const amazon = failed ? unavailable : amazonView(t, result?.amazon);
  const ebay = failed ? unavailable : ebayView(t, result?.ebay, currentLanguage);
  const sales = failed ? [] : result?.ebay?.sales ?? [];
  const updated = formatRelative(t, latestFetchedAt(result));
  const label = <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-dark-400">{t("resale.section")}</p>;

  const empty = noMarketData(amazon, ebay);

  // One view per lookup, so prices-vs-empty rates show whether the card earns its space.
  useEffect(() => {
    if (loading) return;
    track("resale_viewed", {
      retailer,
      outcome: failed ? "failed" : empty ? "empty" : "prices",
      amazon_status: result?.amazon?.status ?? "",
      ebay_status: result?.ebay?.status ?? "",
    });
  }, [loading, result, failed, empty, retailer]);

  if (!loading && empty) return null;

  return (
    <section className="space-y-2">
      {label}
      <div className="grid grid-cols-2 gap-2">
        <MarketCard name={t("resale.amazon")} view={amazon} loading={loading} onLink={() => track("resale_link_clicked", { retailer, market: "amazon" })} />
        <MarketCard name={t("resale.ebay")} view={ebay} loading={loading} onLink={() => track("resale_link_clicked", { retailer, market: "ebay" })} />
      </div>

      {!loading && sales.length > 0 && (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-dark-700 dark:bg-dark-800">
          <button
            type="button"
            onClick={() => {
              if (!salesOpen) track("resale_sales_opened", { retailer, count: sales.length });
              setSalesOpen((v) => !v);
            }}
            aria-expanded={salesOpen}
            className="flex w-full items-center justify-between px-3 py-2.5 text-left"
          >
            <span className="text-[13px] font-semibold text-gray-900 dark:text-white">{t("resale.recent_sales", { count: sales.length })}</span>
            <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${salesOpen ? "rotate-180" : ""}`} />
          </button>
          {salesOpen && (
            <div className="space-y-1.5 border-t border-gray-100 px-3 py-2.5 dark:border-dark-700">
              {sales.map((sale, i) => {
                const meta = [formatShortDate(sale.date, currentLanguage), sale.condition].filter(Boolean).join(" · ");
                return (
                  <div key={`${sale.link || sale.title}-${i}`} className="flex items-center justify-between gap-3 text-xs">
                    <span className="min-w-0">
                      {sale.link ? (
                        <a href={sale.link} target="_blank" rel="noopener noreferrer" onClick={() => track("resale_link_clicked", { retailer, market: "ebay_sale" })} className="block truncate text-gray-700 hover:underline dark:text-dark-200">{sale.title}</a>
                      ) : (
                        <span className="block truncate text-gray-700 dark:text-dark-200">{sale.title}</span>
                      )}
                      {meta && <span className="block text-[11px] text-gray-400 dark:text-dark-500">{meta}</span>}
                    </span>
                    <span className="font-semibold tabular-nums text-gray-900 dark:text-white">{formatUsdCents(sale.price)}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {!loading && updated && <p className="text-[11px] text-gray-400 dark:text-dark-500">{t("resale.footnote", { when: updated })}</p>}
    </section>
  );
}

function MarketCard({ name, view, loading, onLink }: { name: string; view: MarketView; loading: boolean; onLink: () => void }) {
  return (
    <div className="flex min-h-[104px] flex-col rounded-xl border border-gray-200 bg-white p-3 dark:border-dark-700 dark:bg-dark-800">
      <span className="text-[13px] font-bold text-gray-900 dark:text-white">{name}</span>
      {loading ? (
        <div className="mt-2 space-y-2 animate-pulse">
          <div className="h-5 w-3/5 rounded bg-gray-200 dark:bg-dark-700" />
          <div className="h-2.5 w-3/4 rounded bg-gray-200 dark:bg-dark-700" />
        </div>
      ) : view.kind === "price" ? (
        <>
          <span className="mt-1 text-2xl font-extrabold tabular-nums tracking-tight text-gray-900 dark:text-white">{formatUsd(view.price)}</span>
          <span className="text-[11px] text-gray-500 dark:text-dark-400">{view.detail}</span>
        </>
      ) : (
        <span className="mt-2 text-[13px] font-semibold text-gray-500 dark:text-dark-400">{view.message}</span>
      )}
      {!loading && view.link && (
        <span className="mt-auto pt-2">
          <LinkRow label={view.link.label} href={view.link.href} onClick={onLink} />
        </span>
      )}
    </div>
  );
}

function LinkRow({ label, href, onClick }: { label: string; href: string; onClick: () => void }) {
  return (
    <a
      href={href}
      onClick={onClick}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 text-xs font-semibold text-purple-600 hover:text-purple-700 dark:text-purple-300 dark:hover:text-purple-200"
    >
      {label}
      <ExternalLink className="h-3.5 w-3.5" />
    </a>
  );
}
